import { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@voll-intelligence/ui-kit/ui/dialog';
import { useLivePhotoIndex, useLivePhotos } from '../live_photos.js';
import type { HotelPhotoView } from '../models.js';

/** Controle que flutua sobre o véu: setas e X compartilham o tratamento claro
 *  porque o `ghost` de fábrica é ink sobre ink aqui. */
const OVER_VEIL =
  'text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/20 hover:text-primary-foreground';

type HotelGalleryCopy = {
  readonly closeLabel: string;
  readonly previousPhotoLabel: string;
  readonly nextPhotoLabel: string;
};

/** Rolagem sem animação quando a pessoa pediu menos movimento; `smooth` no
 *  resto. Sem `matchMedia` (jsdom) é sempre instantânea. */
function scrollBehavior(): ScrollBehavior {
  if (typeof globalThis.matchMedia !== 'function') return 'instant';
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
}

/** O `N / total`. Dois lugares no cabeçalho, um por faixa de viewport (ao lado
 *  do nome no desktop, centrado e sozinho no celular): o `display:none` do
 *  outro o tira da árvore de acessibilidade, então só o visível anuncia. */
function HotelGalleryCounter({
  selectedIndex,
  total,
  className,
}: {
  readonly selectedIndex: number;
  readonly total: number;
  readonly className: string;
}) {
  return (
    <span
      aria-live="polite"
      data-slot="hotel-gallery-counter"
      className={`text-primary-foreground/60 tabular-nums ${className}`}
    >
      {selectedIndex + 1} / {total}
    </span>
  );
}

/**
 * A faixa de fotos: um quadro por foto, `scroll-snap` horizontal. É o swipe do
 * celular sem gesto à mão — o dedo arrasta a foto de verdade, o navegador
 * assenta no quadro mais perto (issue #3271) — e o mesmo trilho que seta,
 * tecla e miniatura movem por scroll programático.
 *
 * Duas vias que não podem se morder: a seleção segue a faixa (gesto) e a faixa
 * segue a seleção (controles). Um scroll programático de 1 pra 3 passa pelo
 * quadro 2; enquanto a faixa não chega onde foi mandada, o `scroll` é trânsito,
 * não gesto — daí o `pendingIndex`. Um toque no meio do caminho o cancela, pra
 * um swipe interrompendo a animação voltar a contar.
 */
function HotelGalleryStrip({
  photos,
  selectedIndex,
  onSelectedIndexChange,
  onPhotoError,
}: {
  readonly photos: readonly HotelPhotoView[];
  readonly selectedIndex: number;
  readonly onSelectedIndexChange: (index: number) => void;
  readonly onPhotoError: (url: string) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);
  const pendingIndex = useRef<number | null>(null);
  const hasSynced = useRef(false);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || strip.clientWidth === 0 || typeof strip.scrollTo !== 'function') return;
    const frameAt = Math.round(strip.scrollLeft / strip.clientWidth);
    // Já indo pra lá (o snap do próprio gesto assentando): mandar de novo
    // cancelaria a inércia nativa e o quadro daria um tranco.
    if (frameAt === selectedIndex) return;
    // Só o passo vizinho anima; um salto (miniatura longe, a volta cíclica da
    // seta) e a primeira posição ao abrir vão direto — desfilar cinco fotos no
    // caminho não é o que a pessoa pediu.
    const behavior =
      hasSynced.current && Math.abs(frameAt - selectedIndex) === 1 ? scrollBehavior() : 'instant';
    hasSynced.current = true;
    pendingIndex.current = selectedIndex;
    strip.scrollTo({ left: selectedIndex * strip.clientWidth, behavior });
  }, [selectedIndex]);

  const onScroll = () => {
    const strip = stripRef.current;
    if (!strip || strip.clientWidth === 0) return;
    const frameAt = Math.round(strip.scrollLeft / strip.clientWidth);
    if (pendingIndex.current !== null) {
      if (frameAt === pendingIndex.current) pendingIndex.current = null;
      return;
    }
    if (frameAt !== selectedIndex && frameAt >= 0 && frameAt < photos.length)
      onSelectedIndexChange(frameAt);
  };

  return (
    <div
      ref={stripRef}
      data-slot="hotel-gallery-strip"
      className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden [scrollbar-width:none]"
      onScroll={onScroll}
      onPointerDown={() => {
        pendingIndex.current = null;
      }}
    >
      {/* Cada quadro é a célula flex de antes (a foto encolhe ao conteúdo, o
          letterbox é da célula e clicar nele conta como fundo). A calha lateral
          é 1rem no celular — sem setas, a foto ganha a largura — e 4rem no
          desktop, a casa das setas. Só o quadro corrente fica na árvore de
          acessibilidade: os outros são a mesma foto em trânsito. */}
      {photos.map((photo, index) => (
        <div
          key={photo.id}
          aria-hidden={index !== selectedIndex}
          className="flex w-full shrink-0 snap-center items-center justify-center px-4 sm:px-16"
        >
          <img
            className="max-h-full max-w-full rounded-lg object-contain"
            src={photo.url}
            alt={photo.alt}
            loading={Math.abs(index - selectedIndex) <= 1 ? 'eager' : 'lazy'}
            onError={() => {
              onPhotoError(photo.url);
            }}
          />
        </div>
      ))}
    </div>
  );
}

/** As miniaturas do mostruário. A fileira sangra de borda a borda: a folga de
 *  1.5rem é padding INTERNO do scroller (com `scroll-px-6`, a rolagem programática
 *  para na folga, não na borda), e a máscara desvanece as pontas quando há mais.
 *  Centra quando cabe pelas margens automáticas da primeira e da última — um
 *  `justify-center` num conteúdo que estoura deixava as primeiras inalcançáveis.
 *  `pt-1`: o anel da ativa desenha FORA da miniatura. O pé sobe do gesto de
 *  trocar de app do iOS: `max(2rem, safe-area + 1rem)`. */
function HotelGalleryThumbs({
  photos,
  selectedIndex,
  onSelectedIndexChange,
  onPhotoError,
}: {
  readonly photos: readonly HotelPhotoView[];
  readonly selectedIndex: number;
  readonly onSelectedIndexChange: (index: number) => void;
  readonly onPhotoError: (url: string) => void;
}) {
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // A miniatura ativa sempre à vista, centrada: ao navegar, a fileira rola até
  // ela (jsdom não tem `scrollIntoView`; lá a fileira fica onde está).
  useEffect(() => {
    const thumb = thumbRefs.current[selectedIndex];
    if (typeof thumb?.scrollIntoView !== 'function') return;
    thumb.scrollIntoView({ inline: 'center', block: 'nearest', behavior: scrollBehavior() });
  }, [selectedIndex]);

  return (
    <div
      data-slot="hotel-gallery-thumbs"
      className="flex gap-1.5 overflow-x-auto px-6 pt-1 pb-[max(2rem,calc(env(safe-area-inset-bottom)+1rem))] scroll-px-6 [scrollbar-width:none] [mask-image:linear-gradient(to_right,transparent,black_1.5rem,black_calc(100%-1.5rem),transparent)] sm:pb-3 [&>:first-child]:ml-auto [&>:last-child]:mr-auto"
    >
      {photos.map((thumbnail, index) => (
        <button
          key={thumbnail.id}
          ref={(el) => {
            thumbRefs.current[index] = el;
          }}
          type="button"
          /* A foto DA miniatura no rótulo, e a posição entre parênteses: só
               a posição não diz que foto é, e só o alt repete o nome dos
               gatilhos da grade (o fallback do alt é o nome do hotel, igual
               em todas). A `img` fica com `alt=""` pra não dobrar a leitura. */
          aria-label={`${thumbnail.alt} (${String(index + 1)}/${String(photos.length)})`}
          aria-current={index === selectedIndex ? 'true' : undefined}
          className={`relative h-12 w-16 shrink-0 cursor-pointer overflow-hidden rounded-md transition-opacity focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:outline-none ${
            index === selectedIndex
              ? 'ring-2 ring-primary-foreground'
              : 'opacity-50 hover:opacity-90'
          }`}
          onClick={() => {
            onSelectedIndexChange(index);
          }}
        >
          <img
            className="size-full object-cover"
            src={thumbnail.url}
            alt=""
            onError={() => {
              onPhotoError(thumbnail.url);
            }}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * O mostruário: as fotos do hotel em tela cheia, por cima da gaveta e da página.
 *
 * É `Dialog` do kit de propósito — Escape, portal, trava de scroll e devolução do
 * foco vêm de graça, e é o mesmo primitivo do detalhe do quarto. A geometria do
 * `DialogContent` é zerada para ocupar a viewport em faixas: cabeçalho, faixa de
 * fotos e miniaturas. No celular (issue #3271) o cabeçalho desce da barra do
 * sistema, o contador toma o lugar do nome (que segue como nome acessível do
 * diálogo), as setas somem — a troca é por swipe — e a miniatura ativa fica
 * centrada. Véu translúcido e desfocado (não câmara opaca), então **clicar no
 * fundo fecha** — menos sobre o que a pessoa mira de propósito (botão, foto,
 * título, contador).
 */
function HotelGalleryLightbox({
  photos,
  title,
  copy,
  selectedIndex,
  onSelectedIndexChange,
  onPhotoError,
  onClose,
}: {
  readonly photos: readonly HotelPhotoView[];
  readonly title: string;
  readonly copy: HotelGalleryCopy;
  readonly selectedIndex: number;
  readonly onSelectedIndexChange: (index: number) => void;
  readonly onPhotoError: (url: string) => void;
  readonly onClose: () => void;
}) {
  const step = (delta: number) => {
    onSelectedIndexChange((selectedIndex + delta + photos.length) % photos.length);
  };

  if (selectedIndex < 0 || selectedIndex >= photos.length) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        showOverlay={false}
        /* `sm:max-w-none` além do `max-w-none`: o `sm:max-w-sm` de fábrica é de
           outra variante, então o merge não o remove e o mostruário abriria com
           384px. `zoom-*-100` mata o zoom de modal central — a foto cresce da
           miniatura, não da tela.
           `grid-cols-[minmax(0,1fr)]` é a correção da #2669: sem coluna explícita
           a trilha implícita `auto` mede pelo CONTEÚDO, e a fileira de miniaturas
           (9 × 64px + folgas) a estourava pra ~680px num popup de 375px — o
           `max-w-full` do scroller é percentual contra largura intrínseca, logo
           cíclico e ignorado. Com a coluna presa à largura do popup, header,
           faixa e miniaturas voltam a resolver contra a viewport.
           `backdrop-blur-lg` no próprio popup, sem o véu do kit: o popup já
           cobre a viewport inteira com a cor translúcida, e o véu por baixo só
           escureceria a mais. */
        className="grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 rounded-none bg-(--sand-900)/75 p-0 text-primary-foreground ring-0 backdrop-blur-lg top-0 left-0 h-dvh max-h-none w-dvw max-w-none translate-x-0 translate-y-0 sm:max-w-none data-open:zoom-in-100 data-closed:zoom-out-100"
        onKeyDown={(event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          // Sem isto a faixa (um scroller focável) também rolaria com a seta, e
          // o quadro andaria dois.
          event.preventDefault();
          step(event.key === 'ArrowRight' ? 1 : -1);
        }}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          if (
            !target.closest(
              'button, img, [data-slot="dialog-title"], [data-slot="hotel-gallery-counter"]',
            )
          )
            onClose();
        }}
      >
        {/* Três colunas no celular — vazia, contador, X — pra o contador ficar
            no centro exato; no desktop volta a ser a linha nome + contador | X.
            O topo desce da barra do sistema: `max(1rem, safe-area)`, o `Dialog`
            ocupa `h-dvh` sem safe area. */}
        <header className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-6 pt-[max(1rem,env(safe-area-inset-top))] pb-4 sm:flex sm:justify-between">
          {/* O contador é IRMÃO do título, não filho: o nome acessível do diálogo
              sai do `DialogTitle`, e a posição lá dentro renomeava o diálogo a
              cada navegação (com uma região viva dentro do próprio nome). */}
          {/* `min-w-0` + `truncate`: o título tem `leading-none`, então um nome
              longo que QUEBRASSE linha encavalaria as linhas (visto no print da
              #2669). Ele encurta com reticências e o X nunca sai da tela. No
              celular ele é só pra leitor de tela (`max-sm:sr-only`): o que a
              pessoa vê é o contador. */}
          <div className="flex min-w-0 items-baseline gap-3">
            <DialogTitle className="truncate text-primary-foreground max-sm:sr-only">
              {title}
            </DialogTitle>
            <HotelGalleryCounter
              selectedIndex={selectedIndex}
              total={photos.length}
              className="hidden shrink-0 sm:inline"
            />
          </div>
          <HotelGalleryCounter
            selectedIndex={selectedIndex}
            total={photos.length}
            className="text-center sm:hidden"
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={copy.closeLabel}
            className={`justify-self-end ${OVER_VEIL}`}
            onClick={onClose}
          >
            <XIcon />
          </Button>
        </header>
        {/* A célula da faixa tem `min-h-0` (a trilha `minmax(0,1fr)` dá altura
            definida, então os `max-*-full` da foto têm contra o que resolver).
            As setas são do desktop (`hidden sm:inline-flex`): no toque, a troca
            é o swipe da faixa. */}
        <div className="relative flex min-h-0">
          <HotelGalleryStrip
            photos={photos}
            selectedIndex={selectedIndex}
            onSelectedIndexChange={onSelectedIndexChange}
            onPhotoError={onPhotoError}
          />
          {photos.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                aria-label={copy.previousPhotoLabel}
                /* `top-[calc(50%-1rem)]` sem translate: o `active:translate-y-px`
                   do Button substituiria o `-translate-y-1/2` no pressed e a seta
                   despencaria meio botão. */
                className={`absolute top-[calc(50%-1rem)] left-4 hidden sm:inline-flex ${OVER_VEIL}`}
                onClick={() => {
                  step(-1);
                }}
              >
                <ChevronLeftIcon />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={copy.nextPhotoLabel}
                className={`absolute top-[calc(50%-1rem)] right-4 hidden sm:inline-flex ${OVER_VEIL}`}
                onClick={() => {
                  step(1);
                }}
              >
                <ChevronRightIcon />
              </Button>
            </>
          )}
        </div>
        <HotelGalleryThumbs
          photos={photos}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={onSelectedIndexChange}
          onPhotoError={onPhotoError}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Uma foto clicável da grade (gaveta ou mosaico da página). A tile NÃO tem raio
 *  próprio: o conjunto é que é arredondado (`rounded-xl overflow-hidden` no
 *  contêiner, issue #2668) — só os quatro cantos externos, juntas internas retas,
 *  no celular e no desktop. `ring-inset` porque o anel de foco por fora seria
 *  cortado pelo `overflow-hidden` da tile. */
function HotelGalleryTile({
  photo,
  index,
  onOpen,
  onPhotoError,
}: {
  readonly photo: HotelPhotoView;
  readonly index: number;
  readonly onOpen: (index: number) => void;
  readonly onPhotoError: (url: string) => void;
}) {
  return (
    <button
      type="button"
      aria-label={photo.alt}
      className="group focus-visible:ring-ring relative size-full cursor-pointer overflow-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:outline-none"
      onClick={() => {
        onOpen(index);
      }}
    >
      {/* `absolute inset-0`: a img sai do fluxo pra NÃO contribuir com altura
          intrínseca — é o `aspect-[16/9] max-h-72` da foto grande que crava a
          altura da fileira, e a coluna das miniaturas estica nela. Em fluxo com
          `size-full`, a img assumia a altura natural do arquivo e inflava tudo. */}
      <img
        src={photo.url}
        alt={photo.alt}
        className="bg-muted absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        onError={() => {
          onPhotoError(photo.url);
        }}
      />
    </button>
  );
}

/** As colunas/linhas das miniaturas — com menos de quatro elas empilham numa
 *  coluna só (a grade COLAPSA em vez de abrir célula vazia ao lado). */
const PAGE_THUMB_GRID: Record<number, string> = {
  1: 'grid-cols-1 grid-rows-1',
  2: 'grid-cols-1 grid-rows-2',
  3: 'grid-cols-1 grid-rows-3',
  4: 'grid-cols-2 grid-rows-2',
};

/** A galeria da PÁGINA em largura inteira: miniaturas à esquerda (2×2), a foto
 *  grande à direita com o `N fotos`. Outra forma que a galeria da gaveta (grande à
 *  esquerda + duas empilhadas): a mesma pilha em página cheia daria uma foto alta
 *  demais. O lightbox é o mesmo. */
function HotelDetailsGalleryPage({
  photos,
  photosBadgeLabel,
  onOpen,
  onPhotoError,
}: {
  readonly photos: readonly HotelPhotoView[];
  readonly photosBadgeLabel: string;
  readonly onOpen: (index: number) => void;
  readonly onPhotoError: (url: string) => void;
}) {
  const big = photos[0];
  const thumbs = photos.slice(1, 5);
  if (!big) return null;
  return (
    <div className="flex gap-1.5 overflow-hidden rounded-xl">
      {thumbs.length > 0 ? (
        <div className={`grid min-w-0 flex-1 gap-1.5 ${PAGE_THUMB_GRID[thumbs.length] ?? ''}`}>
          {thumbs.map((photo, i) => (
            <HotelGalleryTile
              key={photo.id}
              photo={photo}
              index={i + 1}
              onOpen={onOpen}
              onPhotoError={onPhotoError}
            />
          ))}
        </div>
      ) : null}
      {/* O `aspect-[16/9] max-h-80` mora nesta caixa: é ela que dá a altura da
          fileira (a coluna das miniaturas estica nela, sem altura própria). O
          teto de 20rem é a altura do mosaico no protótipo (~320px). */}
      <div className="relative aspect-[16/9] max-h-80 flex-1">
        <HotelGalleryTile photo={big} index={0} onOpen={onOpen} onPhotoError={onPhotoError} />
        {/* O `N fotos` é a CTA de "ver todas" — só com 2+ fotos: com uma só não há
            galeria a abrir (a própria foto grande já abre o lightbox), e "1 fotos"
            seria agramatical. Por isso só a forma PLURAL do rótulo é necessária. */}
        {photos.length > 1 ? (
          <Button
            variant="secondary"
            size="sm"
            className="absolute right-3 bottom-3 rounded-full shadow-sm"
            onClick={() => {
              onOpen(0);
            }}
          >
            {photos.length} {photosBadgeLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** A grade da GAVETA pela contagem de fotos — o par do `PAGE_THUMB_GRID`. Com
 *  3+ é a geometria de sempre (grande à esquerda, duas empilhadas); com 2 as
 *  duas dividem UMA linha; com 1 a foto ocupa a moldura inteira. A grade fixa
 *  deixava a coluna da direita vazia com uma foto só (#3845). */
const DRAWER_GRID: Record<number, string> = {
  1: 'grid-cols-1 grid-rows-1',
  2: 'grid-cols-[2fr_1fr] grid-rows-1',
  3: 'grid-cols-[2fr_1fr] grid-rows-2',
};

export function HotelDetailsGallery({
  photos,
  title,
  copy,
  variant = 'drawer',
  photosBadgeLabel,
}: {
  readonly photos: readonly HotelPhotoView[];
  readonly title: string;
  readonly copy: HotelGalleryCopy;
  /** `page` troca a grade da gaveta (grande + 2 empilhadas) pelo mosaico da
   *  página (2×2 miniaturas + grande, largura inteira, `N fotos`). */
  readonly variant?: 'drawer' | 'page';
  /** Só a página usa — o rótulo do `N fotos` (ex.: "fotos"). */
  readonly photosBadgeLabel?: string;
}) {
  /* Só as fotos que carregam (#4359): a que falha sai da grade — a seguinte
     ocupa o lugar — do `N fotos` e do mostruário. Sem nenhuma viva a galeria
     some, como a de um hotel sem foto: nada de moldura com imagem quebrada. */
  const { live, fail } = useLivePhotos(photos);
  const [selectedIndex, select] = useLivePhotoIndex(live);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const open = (index: number) => {
    select(index);
    setLightboxOpen(true);
  };

  if (live.length === 0) return null;

  return (
    <section className="genui2 hui-hotel-details-gallery" aria-label={live[0]?.alt}>
      {variant === 'page' ? (
        <HotelDetailsGalleryPage
          photos={live}
          photosBadgeLabel={photosBadgeLabel ?? ''}
          onOpen={open}
          onPhotoError={fail}
        />
      ) : (
        /* A grade da gaveta (grande à esquerda, duas empilhadas à direita) na mesma
           gramática Tailwind/DS do mosaico da página — mesmo `HotelGalleryTile`,
           mesmo raio só na moldura do conjunto. A geometria `2fr 1fr` × 2 linhas em
           `aspect-[2/1]` é a que o CSS `hui-*` tinha; o esqueleto do painel ainda
           usa a classe `hui-hotel-details-gallery-grid` pra espelhá-la. */
        <div
          className={`grid aspect-[2/1] gap-1.5 overflow-hidden rounded-xl ${DRAWER_GRID[Math.min(live.length, 3)] ?? ''}`}
        >
          {live.slice(0, 3).map((photo, index) => (
            <div
              key={photo.id}
              className={index === 0 && live.length >= 3 ? 'row-span-2 min-h-0' : 'min-h-0'}
            >
              <HotelGalleryTile photo={photo} index={index} onOpen={open} onPhotoError={fail} />
            </div>
          ))}
        </div>
      )}
      {lightboxOpen && (
        <HotelGalleryLightbox
          photos={live}
          title={title}
          copy={copy}
          selectedIndex={selectedIndex}
          onSelectedIndexChange={select}
          onPhotoError={fail}
          onClose={() => {
            setLightboxOpen(false);
          }}
        />
      )}
    </section>
  );
}
